import React from "react";
import { useFormik } from "formik";
import { Alert, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import { ErrorLabel, FormActionButtons, Input } from "components";
import { SecurityQuestionsModel } from "models";

import { validationSchema } from "./formUtils";
import { Form, FormContent, GridContainer } from "../Forms.css";
import { PasswordSection } from "./SecurityQuestions.css";

interface SecurityQuestionsFormType extends Partial<SecurityQuestionsModel> {
  requiredCountAnswerError?: string;
}

interface SecurityQuestionsFormProps {
  disableBackButton: boolean;
  defaultValues: SecurityQuestionsFormType;
  onSubmit: (values: Partial<SecurityQuestionsModel>) => void;
  onBack: () => void;
}

export const SecurityQuestionsForm = ({
  onSubmit,
  disableBackButton,
  onBack,
  defaultValues,
}: SecurityQuestionsFormProps) => {
  const { t } = useTranslation();
  const formik = useFormik<SecurityQuestionsFormType>({
    validationSchema,
    initialValues: defaultValues,
    onSubmit: (values: SecurityQuestionsFormType) => {
      const { requiredCountAnswerError, ...cleanedValues } = values;
      onSubmit(cleanedValues);
    },
  });

  const passwordRulesMessage = (
    <>
      <p> {t("min_8_length")}</p>
      <p> {t("number_required")}</p>
      <p> {t("lowercase_required")}</p>
      <p> {t("uppercase_required")}</p>
      <p> {t("special_character_required")}</p>
    </>
  );

  return (
    <Form onSubmit={formik.handleSubmit}>
      <FormContent>
        <Alert severity="info">{t("security_questions_description")}</Alert>
        <GridContainer>
          <Grid item xs={12} md={6}>
            <Input
              label="grandfather_occupation"
              name="grandfatherOccupation"
              formik={formik}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Input
              label="childhood_friend_name"
              name="childhoodFriendName"
              formik={formik}
            />
          </Grid>
        </GridContainer>
        <GridContainer>
          <Grid item xs={12} md={6}>
            <Input
              label="partner_meeting_location"
              name="partnerMeetingLocation"
              formik={formik}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Input
              label="parents_meeting_location"
              name="parentsMeetingLocation"
              formik={formik}
            />
          </Grid>
        </GridContainer>
        <GridContainer>
          <Grid item xs={12} md={6}>
            <Input
              label="preferred_retirement_city"
              name="preferredRetirementCity"
              formik={formik}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Input
              label="favorite_sports_team"
              name="favoriteSportsTeam"
              formik={formik}
            />
          </Grid>
        </GridContainer>
        <GridContainer>
          <Grid item xs={12} md={6}>
            <Input
              label="first_car_model"
              name="firstCarModel"
              formik={formik}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Input label="favorite_food" name="favoriteFood" formik={formik} />
          </Grid>
        </GridContainer>
        {formik.submitCount > 0 && formik.errors.requiredCountAnswerError && (
          <ErrorLabel message="required_count_security_questions" />
        )}
      </FormContent>
      <PasswordSection>
        <Grid item xs={12} md={12}>
          <Input label="password" name="password" formik={formik} />
        </Grid>
        <Alert severity="info">{passwordRulesMessage}</Alert>
      </PasswordSection>
      <FormActionButtons
        disableBackButton={disableBackButton}
        onBack={onBack}
      />
    </Form>
  );
};
